<script setup lang="ts">
/* =============================================================
   BERTH — RATE CARD
   =============================================================
   The berth's rules, top to bottom, as the sentences they are:
   "580 € per 24 h — Ships up to 110 m · any stay". The first
   rule that fits prices the whole stay, so order is shown as a
   number and changed with the arrows -- it is not a field.

   Add and edit open a page of their own (RateFormPage).

   Underneath, "try a price" runs stay_price() itself, so what it
   says is what a booking would be charged, not a second
   implementation of the rules in the browser.
   ============================================================= */

import { onMounted, ref, useTemplateRef, watch } from 'vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import ItemList from '@/components/ui/ItemList.vue'
import ItemRow from '@/components/ui/ItemRow.vue'
import TextField from '@/components/ui/TextField.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import { deleteRate, listRates, quoteStay, swapRates } from '@/lib/rates'
import { to } from '@/config/routes'
import { parseOptionalPositive } from '@/lib/numbers'
import { fromLocalInput, formatEur, rateConditionText, ratePriceText } from '@/lib/bookingLabels'
import { errorMessage } from '@/lib/errors'
import type { BerthRate } from '@/lib/types'

const props = defineProps<{ berthId: number }>()

const rates = ref<BerthRate[]>([])
const loading = ref(true)
const error = ref('')

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    rates.value = await listRates(props.berthId)
  } catch (e) {
    error.value = errorMessage(e, 'Could not load the rate card.')
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(() => props.berthId, load)

/* --- reorder ------------------------------------------------------------- */

const moving = ref(false)

async function move(index: number, by: -1 | 1): Promise<void> {
  const a = rates.value[index]
  const b = rates.value[index + by]
  if (!a || !b || moving.value) return
  moving.value = true
  error.value = ''
  try {
    await swapRates(a, b)
  } catch (e) {
    error.value = errorMessage(e, 'Could not reorder the rules.')
  } finally {
    moving.value = false
    await load()
  }
}

/* --- delete -------------------------------------------------------------- */

const deleting = ref<BerthRate | null>(null)
const confirm = useTemplateRef<InstanceType<typeof ConfirmDialog>>('confirm')

async function onDeleteConfirmed(): Promise<void> {
  if (!deleting.value) return
  try {
    await deleteRate(deleting.value.id)
    deleting.value = null
    await load()
  } catch (e) {
    confirm.value?.fail(errorMessage(e, 'Could not delete the rule.'))
  }
}

/* --- try a price --------------------------------------------------------- */

const tryLength = ref('')
const tryArrival = ref('')
const tryDeparture = ref('')
const quoting = ref(false)
const quoteText = ref('')
const quoteError = ref('')

async function onQuote(): Promise<void> {
  quoteText.value = ''
  quoteError.value = ''
  const arrival = fromLocalInput(tryArrival.value)
  const departure = fromLocalInput(tryDeparture.value)
  if (!arrival || !departure) return void (quoteError.value = 'Pick an arrival and a departure.')
  if (departure <= arrival) return void (quoteError.value = 'Departure must be after arrival.')
  const len = parseOptionalPositive(tryLength.value)
  if (len === undefined) return void (quoteError.value = 'Vessel length must be a number of metres, or blank.')

  quoting.value = true
  try {
    const quote = await quoteStay(props.berthId, len, arrival, departure)
    if (!quote) {
      quoteText.value = 'No rule fits — this berth does not take that ship or stay.'
      return
    }
    const index = rates.value.findIndex((r) => r.id === quote.rate_id)
    const rule = rates.value[index]
    quoteText.value = rule
      ? `${formatEur(quote.price_eur)} — rule ${index + 1}: ${rateConditionText(rule)}`
      : formatEur(quote.price_eur)
  } catch (e) {
    quoteError.value = errorMessage(e, 'Could not price the stay.')
  } finally {
    quoting.value = false
  }
}
</script>

<template>
  <Panel
    flush
    title="Rate card"
    description="The first rule that fits the ship and the stay prices the whole stay. Every period started is charged in full. EUR, net of VAT."
  >
    <template #actions>
      <Button size="sm" icon="plus" :to="to.berthRateNew(berthId)">Add rule</Button>
    </template>

    <Alert v-if="error" tone="danger" class="alert">{{ error }}</Alert>

    <p v-if="loading && rates.length === 0" class="panel-status">Loading…</p>
    <p v-else-if="rates.length === 0" class="panel-status">
      No rules — this berth has no price, so it cannot be booked.
    </p>

    <ItemList v-else>
      <ItemRow v-for="(rate, i) in rates" :key="rate.id">
        <template #title>{{ i + 1 }}. {{ ratePriceText(rate) }}</template>
        {{ rateConditionText(rate) }}
        <template #actions>
          <Button size="sm" variant="ghost" icon="chevronUp" icon-only aria-label="Move up" :disabled="i === 0 || moving" @click="move(i, -1)" />
          <Button size="sm" variant="ghost" icon="chevronDown" icon-only aria-label="Move down" :disabled="i === rates.length - 1 || moving" @click="move(i, 1)" />
          <Button size="sm" variant="ghost" icon="edit" icon-only aria-label="Edit rule" :to="to.berthRateEdit(berthId, rate.id)" />
          <Button size="sm" variant="ghost" icon="trash" icon-only aria-label="Delete rule" @click="deleting = rate" />
        </template>
      </ItemRow>
    </ItemList>

    <form class="tryPrice" @submit.prevent="onQuote">
      <p class="tryTitle">Try a price</p>
      <div class="tryGrid">
        <TextField v-model="tryLength" label="Vessel length" hint="Metres. Blank = unknown." inputmode="decimal" />
        <TextField v-model="tryArrival" label="Arrival" type="datetime-local" />
        <TextField v-model="tryDeparture" label="Departure" type="datetime-local" />
        <Button type="submit" variant="secondary" icon="euro" :loading="quoting" class="tryButton">Price it</Button>
      </div>
      <p v-if="quoteText" class="tryResult">{{ quoteText }}</p>
      <p v-if="quoteError" class="tryError">{{ quoteError }}</p>
    </form>
  </Panel>

  <ConfirmDialog
    ref="confirm"
    :open="deleting !== null"
    title="Delete rule"
    :message="
      deleting
        ? `“${rateConditionText(deleting)} → ${ratePriceText(deleting)}” will be removed. Existing bookings keep their price.`
        : ''
    "
    @close="deleting = null"
    @confirmed="onDeleteConfirmed"
  />
</template>

<style scoped>
.alert {
  margin: var(--space-sm) var(--space-md) 0;
}

.tryPrice {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-md);
  background-color: var(--color-surface-sunken);
  border-top: var(--border-width) solid var(--color-border);
}

.tryTitle,
.tryResult {
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.tryGrid {
  display: grid;
  gap: var(--space-sm);
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  align-items: start;
}

/* Lines the button up with the inputs rather than their labels. */
.tryButton {
  margin-top: 1.6rem;
}

.tryError {
  font-size: var(--text-sm);
  color: var(--color-danger);
}
</style>
